import { useState } from "react";
import {
  ApiRequestError,
  copySalesforceConfig,
  copySubscriberConfig,
  exportSubscriberConfig,
} from "../lib/client";
import type { ConfigDocument, CopyReport } from "../lib/subscriberConfig";
import { customFieldListFlags, MANUAL } from "../lib/types";
import { SubscriberPicker } from "./SubscriberPicker";

/** The name of the action that runs now. No 2 actions run at the same time. */
type Busy =
  | null
  | "export"
  | "copy-dry-run"
  | "copy"
  | "salesforce-dry-run"
  | "salesforce";

interface Credentials {
  auth_url: string;
  client_id: string;
  client_secret: string;
}

const EMPTY_CREDENTIALS: Credentials = {
  auth_url: "",
  client_id: "",
  client_secret: "",
};

/** What `SubscriberCreatedEvent` seeds onto every new subscriber. */
const SEEDED: [string, string][] = [
  [
    "Lifecycle board",
    "Donors, with the columns Prospecting, Cultivating, Requesting, Stewardship",
  ],
  [
    "Opportunity stages",
    "Prospecting, Cultivating, Requesting, Closed Won, Closed Lost",
  ],
  ["Confidence levels", "High 75, Medium 50, Low 25, None 0"],
  [
    "Categories",
    "Donor, Major Donor, Recurring Donor, Board Member, Volunteer, Client, Member, Partner, Foundation, Corporation, Government",
  ],
];

function message(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message;
  return err instanceof Error ? err.message : fallback;
}

/** A command or a mutation to run somewhere else. */
function Cmd({ children }: { children: string }) {
  return <pre className="cmd">{children}</pre>;
}

/**
 * Why the move needs a new subscriber, what a new subscriber already holds, and
 * the 1 thing to confirm with the customer before anything else.
 */
function BeforeYouStart() {
  return (
    <details className="runbook">
      <summary>Before you start</summary>

      <p className="hint">
        A sandbox subscriber and a production subscriber are 2 separate Instil
        subscribers. Each imported record holds a row in{" "}
        <code>external_id_mapping</code> that refers to a Salesforce record id.
        A repoint of the sandbox subscriber leaves each of those rows attached
        to a record of the sandbox org. The sandbox also holds test data that
        the customer does not want in production. A new subscriber starts with
        no mapping rows and no data, and the first import builds both from the
        production org.
      </p>

      <p className="hint">
        Confirm 1 thing with the customer first: whether they entered data in
        Instil that Salesforce did not give them. That data does not carry over.
        An interaction, a task, an opportunity, or a note that a user typed into
        Instil stays with the sandbox subscriber. These commands in
        platform-service-main write such data out to Salesforce, so the first
        production import reads it back:
      </p>
      <Cmd>{`python manage.py export_constituents
python manage.py export_contributions`}</Cmd>

      <p className="hint">
        A new subscriber already holds the values below, because{" "}
        <code>CreateSubscriber</code> seeds them. A customer that kept the
        defaults therefore needs no copy of these. The copy adds only what the
        source holds and the target does not, and it names each seeded value
        that the source dropped, so you can delete it.
      </p>
      <dl className="runbook-list">
        {SEEDED.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <p className="hint">
        1 known gap: <code>Subscriber.timezone</code> is declared on the GraphQL
        type and on both inputs, but nothing ever stores it. The value always
        reads as null. Set the time zone by hand if the customer needs one.
      </p>
    </details>
  );
}

/** The 2 mutations that free the name and the EIN, and then create the target. */
function CreateTheTarget({ sourceName }: { sourceName: string }) {
  const label = sourceName || "<Customer name>";
  return (
    <details className="runbook">
      <summary>The target subscriber does not exist yet</summary>
      <p className="hint">
        This panel does not create a subscriber. Run both mutations below in the
        GraphQL tab, in this order. <code>subscribers.name</code> and{" "}
        <code>subscribers.ein</code> each carry a unique index across every
        subscriber, so the sandbox subscriber must give both up first.
      </p>
      <Cmd>{`mutation FreeTheNameAndTheEin {
  subscribers {
    update(input: {
      id: "<sandbox Global ID>",
      name: "${label} (Salesforce sandbox)",
      ein: null
    }) { errors { ... on DomainError { message } } subscriber { id name } }
  }
}`}</Cmd>
      <Cmd>{`mutation CreateTheProductionSubscriber {
  subscribers {
    create(subscriber: { name: "${label}", ein: "<ein>" }) {
      errors { ... on DomainError { message } }
      subscriber { id name }
    }
  }
}`}</Cmd>
      <p className="hint">
        Then select the new subscriber in the bar at the top of this page. It is
        the target of every step below.
      </p>
    </details>
  );
}

/** The counts of what the source subscriber holds. */
function SourceSummary({ config }: { config: ConfigDocument }) {
  const instilFields = config.customFields.filter((f) => !f.fromSalesforce);
  const salesforceFields = config.customFields.filter((f) => f.fromSalesforce);

  const rows: [string, number][] = [
    ["Lifecycle boards", config.lifecycleBoards.length],
    ["Opportunity stages", config.opportunityStages.length],
    ["Confidence levels", config.opportunityConfidenceLevels.length],
    ["Categories", config.categories.length],
    ["Labels", config.labels.length],
    ["Custom fields (Instil)", instilFields.length],
    ["Custom fields (Salesforce)", salesforceFields.length],
    ["Feature flags", config.featureFlags.length],
    ["LLM prompt overrides", config.llmPromptOverrides.length],
  ];

  const manual = Object.entries(config.manual).filter(([, n]) => n > 0);

  return (
    <>
      <p className="hint">
        <strong>{config.source.name}</strong> holds the values below. The copy
        writes each one that the target does not already hold under the same
        name.
      </p>
      <div className="chip-grid">
        {rows
          .filter(([, count]) => count > 0)
          .map(([label, count]) => (
            <span key={label} className="count-chip">
              {label} <strong>{count}</strong>
            </span>
          ))}
      </div>

      {manual.length > 0 && (
        <>
          <p className="hint">
            The copy does not write the values below. Read each reason, and
            decide what to do. Start with <code>resourceGroups</code> if it
            appears: the ids of a resource group appear on every resource that
            belongs to it, so it goes in before the copy, not after.
          </p>
          <dl className="runbook-list">
            {manual.map(([resource, count]) => (
              <div key={resource}>
                <dt>
                  {resource} <strong>{count}</strong>
                </dt>
                <dd>{MANUAL[resource] ?? "Copy by hand."}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {config.salesforce === null && (
        <p className="hint">
          The source holds no Salesforce connection, so step 3 has nothing to
          write.
        </p>
      )}
    </>
  );
}

/** What a copy wrote, what it skipped, what failed, and what stays manual. */
function Report({ report }: { report: CopyReport }) {
  const wrote = report.steps.filter((s) => s.action !== "skip" && !s.error);
  const skipped = report.steps.filter((s) => s.action === "skip" && !s.error);
  const failed = report.steps.filter((s) => s.error);

  return (
    <div className="proposal">
      <p className="proposal-summary">
        {report.dryRun ? "Dry run — nothing was written. " : "Applied. "}
        Target: <strong>{report.target.name}</strong>. Wrote {wrote.length},
        skipped {skipped.length}, failed {failed.length}.
      </p>

      {failed.length > 0 && (
        <div className="proposal-warnings">
          <strong>Failed</strong>
          <ul>
            {failed.map((step) => (
              <li key={`${step.resource}:${step.detail}`}>
                <code>{step.resource}</code> {step.detail} — {step.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.todo.length > 0 && (
        <div className="proposal-warnings">
          <strong>Still to do by hand</strong>
          <ul>
            {report.todo.map((item) => (
              <li key={item.resource}>
                <code>{item.resource}</code> ({item.count}) — {item.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <details>
        <summary>
          {wrote.length} written, {skipped.length} skipped
        </summary>
        <ul>
          {wrote.map((step) => (
            <li key={`w:${step.resource}:${step.detail}`}>
              <code>{step.action}</code> <code>{step.resource}</code>{" "}
              {step.detail}
            </li>
          ))}
          {skipped.map((step) => (
            <li key={`s:${step.resource}:${step.detail}`} className="muted">
              <code>skip</code> <code>{step.resource}</code> {step.detail}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/**
 * The steps that no part of mechanicus can run. Each one is a management
 * command in platform-service-main or in platform-service-integrations.
 */
function FinishOutsideMechanicus({
  targetId,
  sourceId,
  config,
}: {
  targetId: string;
  sourceId: string;
  config: ConfigDocument | null;
}) {
  // The source subscriber already holds a `salesforce_custom_field_mapping`
  // row for each Salesforce custom field, and the export reads the name out of
  // each row. The command below therefore needs no hand-written field name.
  const namesByObject = config?.salesforce?.customFieldNamesByObject ?? {};
  const { flags, unsupported } = customFieldListFlags(namesByObject);
  const fieldCount = Object.values(namesByObject).flat().length;

  return (
    <details className="runbook">
      <summary>4 steps that run outside mechanicus</summary>

      <h4>1. The custom fields that come from Salesforce</h4>
      <p className="hint">
        The integrations service creates a Salesforce-sourced custom field, and
        no copy does. <code>MapSalesforceFieldsToInstilCustomFields</code>
        creates the Instil definition and the{" "}
        <code>salesforce_custom_field_mapping</code> row together. A copy of the
        definition alone gives a field that no import ever fills. Run, in
        platform-service-integrations:
      </p>
      {flags.length > 0 ? (
        <>
          <Cmd>
            {[
              `python manage.py map_salesforce_fields_to_instil_custom_fields ${targetId}`,
              ...flags,
            ].join(" \\\n  ")}
          </Cmd>
          <p className="hint">
            {fieldCount} field names, read from the mappings of the source. The
            production org must hold a field of the same name on each object, or
            the command reports it as missing.
          </p>
        </>
      ) : (
        <p className="hint">
          {config === null
            ? "Read the source in step 1, and the command appears here with the field names filled in."
            : "The source holds no Salesforce custom field mapping, so this step has nothing to run."}
        </p>
      )}
      {unsupported.length > 0 && (
        <p className="hint error">
          The command takes a list for Contact and for Account only. The source
          also holds custom fields on {unsupported.join(", ")}. Map those by
          hand.
        </p>
      )}

      <h4>2. The import schedule</h4>
      <p className="hint">
        The per-subscriber Salesforce import schedule is not stored. It is built
        from DynamoDB each time <code>upsert_schedules</code> runs, so a new
        subscriber has no schedule until you run, in
        platform-service-integrations:
      </p>
      <Cmd>{"python manage.py upsert_temporal_schedules"}</Cmd>
      <p className="hint">
        <code>upsert_schedules</code> never deletes a schedule. The schedule of
        the sandbox subscriber keeps running against the sandbox after the move.
        Delete{" "}
        <code>salesforce-batch-import-{sourceId || "<sandbox uuid>"}</code> in
        the Temporal UI, or delete the Salesforce credentials of the sandbox
        subscriber, so the sandbox stops importing.
      </p>

      <h4>3. The locked fields</h4>
      <p className="hint">
        This makes Salesforce the source of truth for the fields it owns, so a
        user cannot edit them in Instil and lose the edit at the next import.
        Run, in platform-service-main:
      </p>
      <Cmd>{`python manage.py upsert_default_salesforce_locked_fields --subscriber_id=${targetId}`}</Cmd>

      <h4>4. The first import</h4>
      <p className="hint">
        Start it from the Connection tab, or run, in
        platform-service-integrations:
      </p>
      <Cmd>{`python manage.py start_salesforce_incremental_import ${targetId}`}</Cmd>
      <p className="hint">
        Watch the first run before you turn the Salesforce export on.
      </p>
    </details>
  );
}

/**
 * Copy the configuration of one subscriber onto another.
 *
 * This is the sandbox-to-production move. A customer validates Instil against a
 * Salesforce sandbox. The customer then moves to the production Salesforce org,
 * which needs its own subscriber.
 *
 * The subscriber in the bar at the top of the page is the TARGET. The picker
 * below is the SOURCE. The order of the steps is: read the source, dry run the
 * copy, apply the copy, and then write the Salesforce configuration after the
 * production credentials arrive. The 3 collapsed sections hold the steps that
 * this panel does not run.
 */
export function SubscriberConfigCopy({
  subscriberId,
  subscriberName,
}: {
  subscriberId: string;
  subscriberName: string;
}) {
  const [sourceId, setSourceId] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [config, setConfig] = useState<ConfigDocument | null>(null);
  const [report, setReport] = useState<CopyReport | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] =
    useState<Credentials>(EMPTY_CREDENTIALS);
  // An apply needs a dry run of the same step first. Each of the 2 values below
  // holds the target that the last dry run covered. A change of the target, of
  // the source, or of the credentials makes that dry run meaningless. The
  // comparison against the current target then fails, and the apply button
  // turns off again.
  const [copyLabels, setCopyLabels] = useState(false);
  const [copyDryRunTarget, setCopyDryRunTarget] = useState<string | null>(null);
  const [salesforceDryRunTarget, setSalesforceDryRunTarget] = useState<
    string | null
  >(null);

  const copyDryRunDone = copyDryRunTarget === subscriberId;
  const salesforceDryRunDone = salesforceDryRunTarget === subscriberId;

  const sameSubscriber = Boolean(sourceId) && sourceId === subscriberId;

  async function run(action: Busy, work: () => Promise<void>) {
    setBusy(action);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(message(err, "The request failed."));
    } finally {
      setBusy(null);
    }
  }

  const readSource = () =>
    run("export", async () => {
      const result = await exportSubscriberConfig(sourceId, true);
      setConfig(result);
      setReport(null);
      setCopyDryRunTarget(null);
      setSalesforceDryRunTarget(null);
    });

  const runCopy = (dryRun: boolean) =>
    run(dryRun ? "copy-dry-run" : "copy", async () => {
      if (!config) return;
      const result = await copySubscriberConfig(
        subscriberId,
        config,
        dryRun,
        copyLabels,
      );
      setReport(result);
      setCopyDryRunTarget(dryRun ? subscriberId : null);
    });

  const runSalesforce = (dryRun: boolean) =>
    run(dryRun ? "salesforce-dry-run" : "salesforce", async () => {
      if (!config) return;
      const result = await copySalesforceConfig(
        subscriberId,
        config,
        credentials,
        dryRun,
      );
      setReport(result);
      setSalesforceDryRunTarget(dryRun ? subscriberId : null);
    });

  const credentialsComplete =
    credentials.auth_url.trim() !== "" &&
    credentials.client_id.trim() !== "" &&
    credentials.client_secret.trim() !== "";

  return (
    <section className="panel">
      <h2 className="section-head">Copy a subscriber configuration</h2>
      <p className="hint">
        This moves a customer from a Salesforce sandbox to a Salesforce
        production org. The target is{" "}
        <strong>{subscriberName || subscriberId}</strong>, the subscriber in the
        bar at the top of this page. Pick the source below.
      </p>

      <BeforeYouStart />
      <CreateTheTarget sourceName={sourceName} />

      <h3 className="section-head">1. Read the source</h3>
      <SubscriberPicker
        name="Source subscriber"
        value={sourceName || sourceId}
        onResolve={(id, name) => {
          setSourceId(id);
          setSourceName(name);
          setConfig(null);
          setReport(null);
          setCopyDryRunTarget(null);
          setSalesforceDryRunTarget(null);
        }}
      />
      {sameSubscriber && (
        <div className="banner error">
          The source and the target are the same subscriber.
        </div>
      )}
      <div className="toolbar">
        <button
          type="button"
          className="btn primary"
          onClick={readSource}
          disabled={busy !== null || !sourceId || sameSubscriber}
        >
          {busy === "export" ? "Reading…" : "Read the source"}
        </button>
      </div>

      {config && <SourceSummary config={config} />}

      {config && (
        <>
          <h3 className="section-head">2. Copy the configuration</h3>
          <p className="hint">
            The copy writes the subscriber scalars, the contact info, the AI
            feature toggles, the opportunity stages, the confidence levels, the
            categories, the lifecycle boards with their columns, the custom
            fields that Salesforce did not create, the feature flag memberships,
            and the LLM prompt override. It skips a value that the target
            already holds under the same name, so it is safe to run again after
            you correct something.
          </p>
          <label className="chip-check">
            <input
              type="checkbox"
              checked={copyLabels}
              onChange={(e) => {
                setCopyLabels(e.target.checked);
                setCopyDryRunTarget(null);
              }}
            />
            Copy the {config.labels.length} label(s) too
          </label>
          <p className="hint">
            The Salesforce import makes a label out of each child campaign, and
            it matches an existing label by name. The labels of the source
            therefore name the campaigns of the sandbox org. A copy of them
            leaves a label in the target for each sandbox campaign that the
            production org does not hold, and no import ever deletes one. Leave
            this off unless you know that the customer made the labels by hand.
          </p>
          <p className="hint">
            Run the dry run first, and read every line of the report. The apply
            button stays off until a dry run runs against this target.
          </p>
          <div className="toolbar">
            <button
              type="button"
              className="btn"
              onClick={() => runCopy(true)}
              disabled={busy !== null || sameSubscriber}
            >
              {busy === "copy-dry-run" ? "Running…" : "Dry run"}
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => runCopy(false)}
              disabled={busy !== null || !copyDryRunDone || sameSubscriber}
            >
              {busy === "copy" ? "Copying…" : "Apply the copy"}
            </button>
          </div>
        </>
      )}

      {config?.salesforce && (
        <>
          <h3 className="section-head">3. The Salesforce configuration</h3>
          <p className="hint">
            Run this after the production credentials arrive, and after step 2.
            The 3 fields below belong to the production org. Every other setting
            comes from the source: the import interval, the resources to import,
            the mapping overrides, and the saved filters.
          </p>
          <p className="hint">
            <code>export_to_sf_enabled</code> is set to false. Turn it on in the
            Connection tab only after the first import reads correctly, because
            an export writes to the live Salesforce org.
          </p>
          <p className="hint">
            A mapping of an opportunity stage can hold the UUID of a stage of
            the source subscriber, and the target does not hold that row. The
            copy does not guess. It reports the path of each UUID it found, and
            it does not write that mapping. Correct it in the Mappings tab, and
            prefer the name of the stage over its UUID in every new mapping.
          </p>

          <label className="field">
            <span className="field-label">Auth URL</span>
            <input
              className="text-input"
              spellCheck={false}
              autoComplete="off"
              placeholder="https://login.salesforce.com/services/oauth2/token"
              value={credentials.auth_url}
              onChange={(e) => {
                setCredentials((c) => ({ ...c, auth_url: e.target.value }));
                setSalesforceDryRunTarget(null);
              }}
            />
          </label>
          <label className="field">
            <span className="field-label">Client ID</span>
            <input
              className="text-input"
              spellCheck={false}
              autoComplete="off"
              value={credentials.client_id}
              onChange={(e) => {
                setCredentials((c) => ({ ...c, client_id: e.target.value }));
                setSalesforceDryRunTarget(null);
              }}
            />
          </label>
          <label className="field">
            <span className="field-label">Client secret</span>
            <input
              className="text-input"
              type="password"
              spellCheck={false}
              autoComplete="off"
              value={credentials.client_secret}
              onChange={(e) => {
                setCredentials((c) => ({
                  ...c,
                  client_secret: e.target.value,
                }));
                setSalesforceDryRunTarget(null);
              }}
            />
          </label>

          <div className="toolbar">
            <button
              type="button"
              className="btn"
              onClick={() => runSalesforce(true)}
              disabled={busy !== null || !credentialsComplete || sameSubscriber}
            >
              {busy === "salesforce-dry-run" ? "Running…" : "Dry run"}
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => runSalesforce(false)}
              disabled={
                busy !== null ||
                !credentialsComplete ||
                !salesforceDryRunDone ||
                sameSubscriber
              }
            >
              {busy === "salesforce"
                ? "Writing…"
                : "Write the Salesforce config"}
            </button>
          </div>
        </>
      )}

      {error && <div className="banner error">{error}</div>}
      {report && <Report report={report} />}

      <h3 className="section-head">4. Finish the move</h3>
      <FinishOutsideMechanicus
        targetId={subscriberId}
        sourceId={sourceId}
        config={config}
      />
    </section>
  );
}
